import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

/**
 * Une valeur affichée sur laquelle on peut filtrer d'un clic.
 *
 * Le geste que l'écran doit rendre possible : « je vois cette valeur, montre-moi les
 * autres lignes qui l'ont ». Avant, il fallait la RETAPER dans un champ de la barre de
 * filtres, alors qu'elle était sous les yeux.
 *
 * C'est un vrai `<button>`, pas un `<span onClick>` : accessible au clavier et annoncé
 * comme une action. `stopPropagation` est indispensable — dans un tableau dont la ligne
 * est elle-même cliquable, filtrer ne doit pas ouvrir le détail au passage.
 *
 * `active` : la valeur est DÉJÀ le filtre courant. On la laisse visible (elle informe)
 * mais on retire l'action — cliquer deux fois la même valeur n'a aucun sens et
 * empilerait un doublon.
 *
 * `onRangeSelect` (optionnel) : Maj+clic, réservé aux dates.
 *
 * `variant="badge"` : pour une pastille (statut, action, code licence). Le soulignement
 * du mode texte ne s'y voit PAS — un badge est `inline-flex`, la décoration ne s'y
 * propage pas, et il impose sa propre couleur de texte. Sans ce mode, un badge cliquable
 * ne se distinguait d'un badge inerte que par le curseur. On signale donc par un anneau
 * au survol et au focus, qui épouse la forme arrondie.
 */
export const FilterableValue = ({
  label,
  title,
  onFilter,
  onRangeSelect,
  active = false,
  variant = "text",
  className = "",
}: {
  label: React.ReactNode;
  /** Infobulle — par défaut « Filtrer sur cette valeur ». */
  title?: string;
  onFilter: () => void;
  onRangeSelect?: () => void;
  active?: boolean;
  variant?: "text" | "badge";
  className?: string;
}) => {
  const look =
    variant === "badge"
      ? // `rounded-full` : l'anneau suit la pastille au lieu d'en dessiner un carré.
        "inline-flex rounded-full hover:ring-2 hover:ring-secondary hover:ring-offset-1 focus:outline-none focus:ring-2 focus:ring-tertiary focus:ring-offset-1"
      : "text-left underline decoration-dotted decoration-grey-medium underline-offset-2 hover:decoration-secondary hover:text-secondary focus:outline-none focus:ring-2 focus:ring-tertiary rounded";

  if (active) {
    return (
      <span
        className={`inline-flex items-center gap-1 text-default-text ${className}`}
        title="Filtre déjà actif sur cette valeur"
      >
        {label}
        <FontAwesomeIcon
          icon="filter"
          className="text-secondary text-[0.65rem]"
        />
      </span>
    );
  }

  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        // Maj+clic = étendre en plage (dates uniquement). Convention connue des
        // sélections de plage, du gestionnaire de fichiers au tableur.
        if (e.shiftKey && onRangeSelect) {
          onRangeSelect();
          return;
        }
        onFilter();
      }}
      title={title ?? "Filtrer sur cette valeur"}
      className={`transition ${look} ${className}`}
    >
      {label}
    </button>
  );
};
